import React, { useState, useEffect } from "react";
import {
  DriverTableSegment,
  formatCombinedDriverTableData,
  formatDriverTableData,
  sortCombinedDriverRows,
  sortDriverRows,
} from "../../utils/driverTableUtils";
import { DriverMetricsResponse } from "@/graphql/types/graphql";
import { AnalysisDriverType } from "../../types/types";
import DriverMetricsRow from "./DriverMetricsTableRow";
import EmptyTableBody from "./EmptyTableBody";
import SkeletonHeader from "./SkeletonHeader";

export interface RowSegment {
  range?: string;
  segmentValue?: string;
  data?: DriverMetricsResponse;
  segmentIndex?: number;
}

export interface EditedMetrics {
  [uuid: string]: {
    driver1Percentile?: number;
    driver2Percentile?: number;
    driver1Median?: number;
    driver2Median?: number;
    driver1Avg?: number;
    driver2Avg?: number;
  };
}

interface DriverMetricsTableBodyProps {
  segments: DriverTableSegment[];
  data: DriverMetricsResponse[] | undefined;
  driverMode: AnalysisDriverType;
  driverPercentile: string;
  sortColumn: string;
  sortDirection: "asc" | "desc";
  hasOneDataAndOneManualSegment: boolean;
  isSkeleton: boolean;
  shouldRefetchDriverMetrics: boolean;
  editedPercentiles: EditedMetrics;
  setEditedPercentiles: React.Dispatch<React.SetStateAction<EditedMetrics>>;
}

const DriverMetricsTableBody: React.FC<DriverMetricsTableBodyProps> = ({
  segments,
  data,
  driverMode,
  driverPercentile,
  sortColumn,
  sortDirection,
  hasOneDataAndOneManualSegment,
  isSkeleton,
  shouldRefetchDriverMetrics,
  editedPercentiles,
  setEditedPercentiles,
}) => {
  const [editingCell, setEditingCell] = useState<{ uuid: string; field: string } | null>(null);

  useEffect(() => {
    setEditedPercentiles({});
  }, [data, shouldRefetchDriverMetrics, setEditedPercentiles]);

  const isManualOnly = segments.length === 1 && segments.every((seg) => seg.method === "MANUAL");
  const isDataOnly = segments.length === 1 && segments.every((seg) => seg.method === "DATA");
  const isTwoSameMethod = segments.length === 2 && segments.every((seg) => seg.method === segments[0].method);

  if (isSkeleton) {
    const skeletonColumnCount =
      isManualOnly || isDataOnly || hasOneDataAndOneManualSegment ? 5 : 2 + segments.length * 2 + 1;
    return <SkeletonHeader columnCount={skeletonColumnCount} />;
  }

  if (isManualOnly || isDataOnly) {
    const { displaySegments, rows, isDataOnly: isDataOnlyFromFormat } = formatDriverTableData(segments, data);
    const sortedRows = sortDriverRows(rows, sortColumn, sortDirection, isDataOnlyFromFormat);
    return displaySegments.length > 0 ? (
      <DriverMetricsRow
        rows={sortedRows}
        isManualOnly={isManualOnly}
        driverMode={driverMode}
        editedPercentiles={editedPercentiles}
        setEditedPercentiles={setEditedPercentiles}
        editingCell={editingCell}
        setEditingCell={setEditingCell}
      />
    ) : (
      <EmptyTableBody colSpan={5} />
    );
  }

  if (hasOneDataAndOneManualSegment) {
    const { combinedRows } = formatCombinedDriverTableData(segments, data);
    const sortedRows = sortCombinedDriverRows(combinedRows, sortColumn, sortDirection);
    return sortedRows.length > 0 ? (
      <DriverMetricsRow
        rows={sortedRows}
        hasOneDataAndOneManualSegment
        driverMode={driverMode}
        editedPercentiles={editedPercentiles}
        setEditedPercentiles={setEditedPercentiles}
        editingCell={editingCell}
        setEditingCell={setEditingCell}
      />
    ) : (
      <EmptyTableBody colSpan={5} />
    );
  }

  if (isTwoSameMethod) {
    const { displaySegments, rows, isDataOnly: isDataOnlyFromFormat } = formatDriverTableData(segments, data);
    const sortedRows = sortDriverRows(rows, sortColumn, sortDirection, isDataOnlyFromFormat);
    return displaySegments.length > 0 ? (
      <DriverMetricsRow
        rows={sortedRows}
        isTwoSameMethod
        driverMode={driverMode}
        segments={segments}
        editedPercentiles={editedPercentiles}
        setEditedPercentiles={setEditedPercentiles}
        editingCell={editingCell}
        setEditingCell={setEditingCell}
      />
    ) : (
      <EmptyTableBody colSpan={5} />
    );
  }

  const { displaySegments, rows, isDataOnly: isDataOnlyFromFormat } = formatDriverTableData(segments, data);
  const sortedRows = sortDriverRows(rows, sortColumn, sortDirection, isDataOnlyFromFormat);
  return displaySegments.length > 0 ? (
    <DriverMetricsRow
      rows={sortedRows}
      segments={segments}
      driverMode={driverMode}
      editedPercentiles={editedPercentiles}
      setEditedPercentiles={setEditedPercentiles}
      editingCell={editingCell}
      setEditingCell={setEditingCell}
    />
  ) : (
    <EmptyTableBody colSpan={2 + segments.length * 2 + 1} />
  );
};

export default DriverMetricsTableBody;
